import { useEffect, useReducer, useState } from 'react';

export enum FetchAction {
  Init = 'INIT',
  Success = 'SUCCESS',
  Failure = 'FAILURE'
}

export type FetchState<T = unknown> = {
  isLoading: boolean;
  isError: boolean;
  data?: T;
  error?: string;
};

export const fetchDefaultState = {
  isLoading: false,
  isError: false,
  data: undefined,
  error: undefined
};

type FetchStateAction =
  | { type: FetchAction.Init }
  | { type: FetchAction.Success; payload: unknown }
  | { type: FetchAction.Failure; payload: string };

export const fetchReducer = (state: FetchState, action: FetchStateAction) => {
  switch (action.type) {
    case FetchAction.Init:
      return {
        ...state,
        isLoading: true,
        isError: false
      };
    case FetchAction.Success:
      return {
        ...state,
        isLoading: false,
        isError: false,
        data: action.payload
      };
    case FetchAction.Failure:
      return {
        ...state,
        isLoading: false,
        isError: true,
        error: action.payload
      };
    default:
      throw new Error();
  }
};

export type FetchRequestData = {
  url: RequestInfo;
  options?: RequestInit;
};

export function useFetch<T = unknown>(requestData?: FetchRequestData) {
  const [request, setRequest] = useState<FetchRequestData | undefined>(
    requestData
  );
  const [state, dispatch] = useReducer(fetchReducer, fetchDefaultState);

  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      if (!request) {
        return;
      }

      dispatch({ type: FetchAction.Init });

      try {
        const response = await fetch(request.url, request.options);
        const json = await response.json();

        if (!didCancel) {
          dispatch({ type: FetchAction.Success, payload: json });
        }
      } catch (error: any) {
        if (!didCancel) {
          dispatch({ type: FetchAction.Failure, payload: error.toString() });
        }
      }
    };

    fetchData();

    return () => {
      didCancel = true;
    };
  }, [request]);

  return [state, setRequest] as [FetchState<T>, typeof setRequest];
}
